<template>
  <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
    <!-- Título y Badges -->
    <div>
      <div class="flex items-center gap-2.5 mb-1">
        <h1 class="text-3xl font-bold text-gray-800 tracking-tight">{{ title }}</h1>
        <span class="px-2.5 py-0.5 text-xs font-black uppercase tracking-wider rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-sm">
          PRO
        </span>
        <span 
          v-if="totalEmployees !== null && totalEmployees !== undefined" 
          class="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-orange-100 text-orange-700"
        >
          {{ totalEmployees }} empleados
        </span>
      </div>
      <p class="text-xs sm:text-sm text-gray-500">
        Gestión de personal, registro de turnos y trabajo a destajo con liquidación conectada a Compras y Gastos.
      </p>
    </div>

    <!-- Barra de Acciones del Header -->
    <div class="flex flex-wrap items-center gap-2.5 w-full sm:w-auto justify-end">
      <!-- Botón Exportar (Dropdown) -->
      <div ref="exportDropdownRef" class="relative">
        <button 
          type="button"
          class="px-3.5 py-2 bg-white border border-gray-300 hover:border-gray-400 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium shadow-sm text-sm cursor-pointer"
          @click.stop="toggleExportDropdown"
        >
          <i class="fa fa-download text-gray-500" />
          <span>Exportar</span>
          <i 
            class="fa fa-chevron-down text-[10px] text-gray-400 transition-transform duration-200" 
            :class="{'rotate-180': isExportOpen}"
          />
        </button>

        <!-- Dropdown Menu -->
        <div 
          v-if="isExportOpen" 
          class="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 z-50 py-1.5 animate-in fade-in zoom-in-95 duration-150 overflow-hidden"
        >
          <a 
            :href="excelExportUrl" 
            target="_blank"
            class="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
            @click="isExportOpen = false"
          >
            <div class="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
              <i class="fa fa-file-excel text-emerald-600 text-sm" />
            </div>
            <div class="text-left">
              <div class="font-bold text-xs text-gray-900">Exportar Finanzas (Excel)</div>
              <div class="text-[10px] text-gray-400">Montos en USD, Bs y datos bancarios</div>
            </div>
          </a>

          <a 
            :href="pdfExportUrl" 
            target="_blank"
            class="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors border-t border-gray-50"
            @click="isExportOpen = false"
          >
            <div class="w-7 h-7 rounded-lg bg-purple-50 flex items-center justify-center shrink-0">
              <i class="fa fa-file-pdf text-purple-600 text-sm" />
            </div>
            <div class="text-left">
              <div class="font-bold text-xs text-gray-900">Reporte Nómina (PDF)</div>
              <div class="text-[10px] text-gray-400">Planilla consolidada para administración</div>
            </div>
          </a>
        </div>
      </div>

      <!-- Botón Secundario: Turnos Masivos -->
      <button 
        type="button"
        class="px-3.5 py-2 bg-white border border-gray-300 hover:border-gray-400 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium shadow-sm text-sm cursor-pointer"
        title="Registrar turnos para varios empleados a la vez"
        @click="$emit('open-bulk-shifts')"
      >
        <i class="fa fa-users text-amber-500" />
        <span>Turnos Masivos</span>
      </button>

      <!-- Botón Secundario: + Turno -->
      <button 
        type="button"
        class="px-3.5 py-2 bg-white border border-gray-300 hover:border-gray-400 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium shadow-sm text-sm cursor-pointer"
        title="Registrar turno individual"
        @click="$emit('open-single-shift')"
      >
        <i class="fa fa-calendar-plus text-blue-500" />
        <span>+ Turno</span>
      </button>

      <!-- Botón Secundario: + Personal -->
      <button 
        type="button"
        class="px-3.5 py-2 bg-white border border-gray-300 hover:border-gray-400 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium shadow-sm text-sm cursor-pointer"
        title="Registrar nuevo empleado"
        @click="$emit('open-employee-modal')"
      >
        <i class="fa fa-user-plus text-teal-600" />
        <span>+ Personal</span>
      </button>

      <!-- Botón Primario: Liquidar Pago (Naranja Corporativo) -->
      <button 
        type="button"
        class="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center gap-2 transition-colors font-medium shadow-sm text-sm cursor-pointer active:scale-95"
        @click="$emit('open-settlement-modal')"
      >
        <i class="fa fa-hand-holding-usd" />
        <span>Liquidar Pago</span>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PaysheetHeader',

  props: {
    title: {
      type: String,
      default: 'Nómina y Destajo',
    },
    totalEmployees: {
      type: Number,
      default: 0,
    },
    excelExportUrl: {
      type: String,
      default: '/paysheet/export/excel',
    },
    pdfExportUrl: {
      type: String,
      default: '/paysheet/export/pdf',
    },
  },

  emits: [
    'open-bulk-shifts',
    'open-single-shift',
    'open-employee-modal',
    'open-settlement-modal',
  ],

  data() {
    return {
      isExportOpen: false,
    }
  },

  mounted() {
    document.addEventListener('click', this.handleOutsideClick)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick)
  },

  methods: {
    toggleExportDropdown() {
      this.isExportOpen = !this.isExportOpen
    },

    handleOutsideClick(event) {
      if (this.isExportOpen && this.$refs.exportDropdownRef && !this.$refs.exportDropdownRef.contains(event.target)) {
        this.isExportOpen = false
      }
    },
  },
}
</script>
